<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useUserStore } from '@/store/user';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const userStore = useUserStore();
const accountArea = ref<HTMLElement | null>(null);
const showAccountMenu = ref(false);
const showLogoutConfirm = ref(false);

const accountLabel = computed(() => {
  if (!authStore.token) return '登录';
  return userStore.userInfo?.username || userStore.userInfo?.phone || '账号';
});

const handleBackHome = () => {
  router.push('/');
};

const handleAccount = () => {
  if (authStore.token) {
    showAccountMenu.value = !showAccountMenu.value;
    return;
  }
  router.push('/creator');
};

const requestLogout = () => {
  showAccountMenu.value = false;
  showLogoutConfirm.value = true;
};

const cancelLogout = () => {
  showLogoutConfirm.value = false;
};

const confirmLogout = () => {
  authStore.clearToken();
  userStore.clearUserInfo();
  showLogoutConfirm.value = false;
  showAccountMenu.value = false;
  if (route.name === 'CreatorCenter') router.replace('/creator');
};

const handleDocumentClick = (event: MouseEvent) => {
  if (!accountArea.value?.contains(event.target as Node)) showAccountMenu.value = false;
};

onMounted(() => {
  if (authStore.token && !userStore.userInfo) userStore.loadUserInfo();
  document.addEventListener('click', handleDocumentClick);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
});
</script>

<template>
  <div class="app">
    <header>
      <div class="app__info" @click="handleBackHome">
        <img class="app__info--logo" src="@/assets/images/home/logo.png" alt="" />
        <div class="app__info--label">
          <span>萌屿</span>
          <span>moeisland</span>
        </div>
      </div>
      <nav>
        <router-link to="/">首页</router-link>
        <router-link to="/creator">创作者中心</router-link>
        <router-link :to="{ name: 'ShopApplyGuide' }">装扮规范</router-link>
      </nav>
      <div ref="accountArea" class="app__account-wrap">
        <button class="app__account" :class="{ active: authStore.token }" @click="handleAccount">{{ accountLabel }}</button>
        <div v-if="authStore.token && showAccountMenu" class="app__account-menu">
          <button @click="requestLogout">退出登录</button>
        </div>
      </div>
      <span v-if="authStore.token" class="app__avatar">{{ userStore.isCreator ? '创' : '用' }}</span>
    </header>
    <section>
      <img class="bg" draggable="false" src="@/assets/images/home/bg.png" alt="" />
      <div class="app__content" :class="{ 'app__content--plain': route.name === 'CreatorCenter' }">
        <router-view></router-view>
      </div>
    </section>
    <footer v-if="route.name !== 'CreatorCenter' && route.name !== 'ShopApplyGuide'">
      <span>
        <a target="_blank" :href="$router.resolve({ name: 'Protocol', params: { type: 'user_agreement' } }).href">用户协议</a>
      </span>
      <span>
        <a target="_blank" :href="$router.resolve({ name: 'Protocol', params: { type: 'privacy_agreement' } }).href">隐私协议</a>
      </span>
      <span>
        <a target="_blank" href="http://beian.miit.gov.cn/">ICP备案号：沪ICP备2023026901号-4</a>
      </span>
    </footer>
    <div v-if="showLogoutConfirm" class="app__modal-mask" @click.self="cancelLogout">
      <div class="app__confirm-modal">
        <h2>确认退出登录？</h2>
        <p>退出后需要重新登录萌屿账号才能进入创作者中心。</p>
        <div class="app__confirm-actions">
          <button class="app__confirm-secondary" @click="cancelLogout">取消</button>
          <button class="app__confirm-primary" @click="confirmLogout">确认退出</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app {
  width: 100%;
  height: 100%;
  overflow: hidden;

  header {
    width: 100%;
    height: 80px;
    background-color: #fff;
    padding: 0 260px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 60px;
  }

  section {
    width: 100%;
    height: calc(100% - 80px);
    position: relative;
  }

  footer {
    position: absolute;
    bottom: 32px;
    left: 50%;
    transform: translate(-50%);
    text-align: center;
    display: flex;
    justify-content: center;
    z-index: 9;
    > :last-child {
      border-right: none;
    }
    > span {
      font-size: 12px;
      color: #999999;
      padding: 0 8px;
      border-right: 1px solid #999999;
      cursor: pointer;
      a {
        font-style: normal;
        text-decoration: none;
        color: #999999;
      }
    }
  }

  &__info {
    display: flex;
    align-items: center;
    width: fit-content;
    height: 100%;
    cursor: pointer;
    &--logo {
      width: 50px;
      height: 50px;
    }
    &--label {
      margin-left: 10px;
      display: flex;
      align-items: center;
      font-weight: bold;
      color: #333333;
      > :nth-child(1) {
        font-size: 22px;
      }
      > :nth-child(2) {
        margin-left: 5px;
        font-size: 20px;
      }
    }
  }

  nav {
    display: flex;
    gap: 46px;
    align-items: center;

    a {
      color: #1f1d29;
      font-size: 16px;
      text-decoration: none;

      &.router-link-active {
        font-weight: 600;
      }
    }
  }

  &__account-wrap {
    margin-left: auto;
    position: relative;
    flex: 0 0 auto;
  }

  &__account {
    min-width: 88px;
    height: 46px;
    padding: 0 20px;
    border: 2px solid #17151f;
    border-radius: 8px;
    background: #fff;
    color: #1f1d29;
    font-size: 16px;
    cursor: pointer;
    white-space: nowrap;

    &.active {
      min-width: 106px;
      max-width: 180px;
      height: 34px;
      padding: 0 18px;
      border: 1px solid #ff8ab8;
      border-radius: 999px;
      color: #fb5a9b;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__account-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 10px);
    min-width: 128px;
    padding: 8px;
    border: 1px solid #f0dce9;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 12px 28px rgba(54, 39, 58, 0.14);
    z-index: 20;

    button {
      width: 100%;
      height: 38px;
      border: 0;
      border-radius: 6px;
      background: transparent;
      color: #1f1d29;
      font-size: 15px;
      cursor: pointer;
      text-align: left;
      padding: 0 12px;
      white-space: nowrap;

      &:hover {
        background: #fff1f7;
        color: #fb5a9b;
      }
    }
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid #1f1d29;
    background: #cff4ee;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  &__content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }

  &__content--plain {
    overflow: auto;
  }

  &__modal-mask {
    position: fixed;
    inset: 0;
    z-index: 100;
    background: rgba(18, 15, 22, 0.28);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
  }

  &__confirm-modal {
    width: min(420px, 100%);
    border-radius: 8px;
    background: #fff;
    padding: 32px;
    box-shadow: 0 18px 48px rgba(35, 22, 40, 0.2);
    box-sizing: border-box;
    text-align: center;

    h2 {
      margin: 0 0 12px;
      color: #17151f;
      font-size: 24px;
    }

    p {
      margin: 0;
      color: #746b78;
      font-size: 16px;
      line-height: 1.6;
    }
  }

  &__confirm-actions {
    margin-top: 28px;
    display: flex;
    justify-content: center;
    gap: 14px;
  }

  &__confirm-secondary,
  &__confirm-primary {
    min-width: 120px;
    height: 42px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    white-space: nowrap;
  }

  &__confirm-secondary {
    border: 1px solid #1f1d29;
    background: #fff;
    color: #1f1d29;
  }

  &__confirm-primary {
    border: 0;
    background: #fb5a9b;
    color: #fff;
    box-shadow: 4px 4px 0 #17151f;
  }

  @media (min-width: 768px) and (max-width: 1199px) {
    header {
      height: 4.5rem;
      padding: 0 2.25rem;
      gap: 1.5rem;
    }

    section {
      height: calc(100% - 4.5rem);
    }

    &__info--logo {
      width: 2.75rem;
      height: 2.75rem;
    }

    &__info--label {
      margin-left: 0.5rem;

      > :nth-child(1) {
        font-size: 1.25rem;
      }

      > :nth-child(2) {
        font-size: 1.125rem;
      }
    }

    nav {
      gap: 1.5rem;

      a {
        font-size: 0.9375rem;
        white-space: nowrap;
      }
    }

    &__account {
      min-width: 5.75rem;
      height: 2.25rem;
      padding: 0 0.875rem;
      font-size: 0.875rem;

      &.active {
        min-width: 6.25rem;
        max-width: 8rem;
        height: 2.25rem;
        border-color: #ff8ab8;
        background: #fff8fb;
        font-weight: 600;
      }
    }

    &__avatar {
      width: 2.25rem;
      height: 2.25rem;
      margin-left: -0.5rem;
      font-size: 0.875rem;
    }
  }

  @media (max-width: 767px) {
    overflow: auto;

    header {
      position: sticky;
      top: 0;
      z-index: 10;
      height: 3.5rem;
      padding: 0 0.75rem;
      gap: 0.75rem;
    }

    section {
      height: auto;
      min-height: calc(100vh - 3.5rem);
    }

    footer {
      position: static;
      transform: none;
      padding: 0 1rem 1.25rem;
      box-sizing: border-box;
      flex-wrap: wrap;
      gap: 0.35rem 0;

      > span {
        font-size: 0.68rem;
        line-height: 1.4;
      }
    }

    &__info--logo {
      width: 2rem;
      height: 2rem;
    }

    &__info--label {
      margin-left: 0.4rem;
      display: block;

      > :nth-child(1),
      > :nth-child(2) {
        display: block;
        margin-left: 0;
        font-size: 0.72rem;
        line-height: 1.1;
        white-space: nowrap;
      }
    }

    nav {
      flex: 1;
      justify-content: center;
      gap: 0.7rem;

      a {
        font-size: 0.72rem;
        white-space: nowrap;
      }
    }

    &__account-wrap {
      margin-left: 0;
    }

    &__account {
      min-width: 3rem;
      height: 1.75rem;
      padding: 0 0.65rem;
      border-width: 0.1rem;
      border-radius: 0.45rem;
      font-size: 0.72rem;

      &.active {
        min-width: 4.7rem;
        max-width: 5.8rem;
        height: 1.75rem;
      }
    }

    &__account-menu {
      right: 0;
      top: calc(100% + 0.45rem);
      min-width: 6.4rem;

      button {
        font-size: 0.8rem;
      }
    }

    &__avatar {
      display: none;
    }

    &__content {
      position: relative;
      min-height: calc(100vh - 3.5rem);
      z-index: auto;
    }

    &__content--plain {
      overflow: visible;
    }
  }
}
</style>
